import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Copy, CreditCard } from 'lucide-react';
import { colors as theme, fonts } from '../theme';
import ConfirmDialog from '../ui/ConfirmDialog';
import { toastError, toastSuccess } from '../ui/toast';
import {
  loadMyCoachPlan, declineRequest, coachBilling, acceptMessage, pounds, billFor, when,
} from '../services/coachPlanService';

// A paying coach's Plan & Code page (plan 014, step 4; docs/plans/014-states.md). Desktop-first, as every coach
// screen (owner, 21 October 2026). It shows what the database says (migration 111) and sends anything that touches
// money to the server; it never decides a state itself.

const card = {
  backgroundColor: theme.surface1, border: `1px solid ${theme.surface3}`, borderRadius: '14px', padding: '20px',
};
const label = { color: theme.textMuted, fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' };
const button = (kind = 'primary') => ({
  padding: '10px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  border: kind === 'primary' ? 'none' : `1px solid ${kind === 'danger' ? theme.danger : theme.borderStrong}`,
  backgroundColor: kind === 'primary' ? theme.gold : 'transparent',
  color: kind === 'primary' ? theme.bg : kind === 'danger' ? theme.danger : theme.text,
});
const TONE = {
  good: { border: theme.success, bg: theme.successDim },
  info: { border: 'rgba(212,175,55,0.45)', bg: theme.goldDim },
  bad: { border: theme.danger, bg: theme.dangerDim },
};

function Notice({ tone = 'info', title, children, action }) {
  return (
    <div role={tone === 'bad' ? 'alert' : 'status'} style={{
      border: `1px solid ${TONE[tone].border}`, backgroundColor: TONE[tone].bg, borderRadius: '12px',
      padding: '14px 16px', marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap',
    }}>
      <div style={{ flex: '1 1 320px' }}>
        {title && <div style={{ color: theme.text, fontWeight: 700, marginBottom: '4px' }}>{title}</div>}
        <div style={{ color: theme.text, fontSize: '14px', lineHeight: 1.5 }}>{children}</div>
      </div>
      {action}
    </div>
  );
}

// Where the plan stands, in a sentence or two, with the one thing the coach can do about it. The clock decides as
// well as the stored state, because the jobs that record a change can be late (Codex's review, PR 304).
function StateNotice({ plan, busy, onCard, onStay, onCheckPayment, onOtherCard }) {
  const s = plan.state;
  const first = pounds(plan.first_charge_pence);
  if (plan.status === 'ended' || plan.access_ended) {
    return (
      <Notice tone="bad" title="Your plan has ended">
        It ended on {when(plan.status === 'ended' ? plan.ended_at : plan.plan_end)}. Your code no longer works, and your clients have
        7 days to choose what happens next. Please contact Cipher if you would like to come back.
      </Notice>
    );
  }
  if (s === 'no_card') {
    return (
      <Notice title="Add a card to start" action={<button type="button" style={button()} disabled={busy} onClick={onCard}>Add card</button>}>
        Nothing is charged until you accept your first client. Your code starts working once your card is saved.
      </Notice>
    );
  }
  if (s === 'ready') {
    return <Notice>Your card is saved. Your first charge, {first}, comes when you accept your first client.</Notice>;
  }
  if (s === 'paused') {
    return <Notice>You have no clients, so billing is paused. Accepting a client starts it again with a first charge of {first}.</Notice>;
  }
  if (s === 'charging') {
    if (plan.attempt?.status === 'needs_owner') {
      return <Notice title="We are checking a payment by hand">Cipher has been told and will be in touch. Waiting clients stay waiting until their requests run out.</Notice>;
    }
    return (
      <Notice
        title="Your first payment is going through"
        action={(
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" style={button()} disabled={busy} onClick={onCheckPayment}>Check payment</button>
            <button type="button" style={button('quiet')} disabled={busy} onClick={onOtherCard}>Use another card</button>
          </div>
        )}
      >
        Your client joins once it has gone through. If your bank wants you to approve it, Check payment gives you the link.
      </Notice>
    );
  }
  if (s === 'unpaid' || plan.renewal === 'unanswered') {
    const why = s === 'unpaid'
      ? ({ bank_approval: 'Your bank wants you to approve your payment.', unanswered: 'We have not heard back about your payment.' }[plan.unpaid_reason]
        || 'Your payment failed.')
      : 'We have not heard back about your renewal payment.';
    return (
      <Notice tone="bad" title={why} action={<button type="button" style={button()} disabled={busy} onClick={onCard}>Update card</button>}>
        Please pay by <strong>{when(plan.deadline)}</strong>. Until then you keep working with your clients, and they notice nothing,
        but you cannot accept new ones. After that your plan ends and your clients get 7 days to choose what happens next.
      </Notice>
    );
  }
  if (plan.renewal === 'charging_now') {
    return <Notice>Your monthly renewal is being charged now. This page shows the result once the bank has answered.</Notice>;
  }
  if (s === 'cancelling') {
    return (
      <Notice title="You have chosen to stop paying" action={<button type="button" style={button()} disabled={busy} onClick={onStay}>Stay with Cipher</button>}>
        Your plan ends on {when(plan.paid_through)}, and you will not be billed again. Your clients then get 7 days to choose what happens next.
        You can change your mind until then.
      </Notice>
    );
  }
  if (s === 'stopping') {
    return <Notice>You have no clients right now, so billing stops at your renewal on {when(plan.paid_through, false)}. It starts again when you accept a new client.</Notice>;
  }
  return null;
}

// What Accept does for a client who has never paid, in words: the bill it leads to, or a charge now
function whatItDoes(kind, r, plan) {
  const first = pounds(plan.first_charge_pence);
  if (kind === 'first_charge') return `Accept charges ${first} now, and they join once it has gone through`;
  if (kind === 'resume') {
    return `your plan carries on and your next bill becomes ${pounds(r.next_bill_after_pence)}. If your billing has already stopped, `
      + `Accept charges ${first} now instead, and they join once it has gone through`;
  }
  return r.next_bill_after_pence === plan.next_bill_pence
    ? 'no change to your next bill'
    : `your next bill becomes ${pounds(r.next_bill_after_pence)}`;
}

// What Accept does for this request, in words, and whether it can be pressed. A possible charge is said before the
// press (Codex's review, PR 304, P1).
function acceptLine(r, plan) {
  const first = pounds(plan.first_charge_pence);
  switch (r.accept) {
    case 'adds':
      return { what: `New client · ${whatItDoes('adds', r, plan)}`, button: 'Accept' };
    case 'first_charge':
      return { what: `New client · ${whatItDoes('first_charge', r, plan)}`, button: `Accept and pay ${first}` };
    case 'resume':
      return { what: `New client · ${whatItDoes('resume', r, plan)}`, button: 'Accept' };
    case 'no_place':
      return { what: 'Pays for themselves · costs you nothing', button: 'Accept' };
    case 'check_first':
      return {
        what: `We check their account when you accept. If they have paid before, they cost you nothing; if not, ${whatItDoes(r.if_new, r, plan)}`,
        button: r.if_new === 'first_charge' ? `Accept (may charge ${first})` : 'Accept',
      };
    case 'payment_under_way':
      return r.accepted
        ? { what: 'Accepted · joins once your first payment has gone through', button: null }
        : { what: 'Waits until your first payment has gone through', button: null };
    case 'full':
      return { what: 'You have reached your limit. Please contact Cipher', button: null };
    default:
      return { what: 'You cannot accept new clients right now', button: null };
  }
}

function Calculator({ price, start }) {
  const [n, setN] = useState(Math.max(start, 1));
  const clients = Math.min(Math.max(Number.isFinite(n) ? n : 0, 0), price.safety_stop);
  return (
    <div style={card}>
      <div style={label}>Bill calculator</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
        <label htmlFor="coach-calc" style={{ color: theme.text, fontSize: '14px' }}>With</label>
        <input
          id="coach-calc" type="number" min={0} max={price.safety_stop} value={Number.isFinite(n) ? n : ''}
          onChange={(e) => setN(e.target.value === '' ? NaN : Number(e.target.value))}
          style={{ width: '80px', padding: '8px 10px', borderRadius: '8px', border: `1px solid ${theme.borderStrong}`,
            backgroundColor: theme.surface2, color: theme.text, fontSize: '15px', fontFamily: fonts.mono }}
        />
        <span style={{ color: theme.text, fontSize: '14px' }}>clients, a month costs</span>
        <span style={{ color: theme.gold, fontSize: '22px', fontWeight: 700, fontFamily: fonts.mono }}>{pounds(billFor(price, clients))}</span>
      </div>
      <div style={{ color: theme.textMuted, fontSize: '12px', marginTop: '10px', lineHeight: 1.5 }}>
        {pounds(price.base_pence)} a month includes {price.included} clients; each extra client is {pounds(price.extra_pence)}.
        An estimate: each bill uses the highest number of clients you had during the month before it.
      </div>
    </div>
  );
}

const CoachPlanPage = ({ onBack, cardReturn = null, onChanged }) => {
  const [plan, setPlan] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(null);          // what is being done: 'card', 'leave', a place id, …
  const [result, setResult] = useState(null);      // the last answer worth keeping on screen
  const [confirm, setConfirm] = useState(null);    // 'leave' or a request to decline
  const looks = useRef(0);
  // Kept in a ref, so a new function from the parent on each render does not reload the page
  const changed = useRef(onChanged);
  changed.current = onChanged;

  const load = useCallback(async () => {
    try {
      const out = await loadMyCoachPlan();
      setPlan(out);
      setFailed(false);
      changed.current?.(out);
      return out;
    } catch (err) {
      console.error('[coach-plan] could not load:', err.message);
      setFailed(true);
      return null;
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Back from Stripe's card page: Stripe tells the server, which records the card a moment later
  useEffect(() => {
    if (cardReturn === 'cancelled') setResult({ tone: 'info', text: 'No card was added.' });
    if (cardReturn !== 'saved') return undefined;
    setResult({ tone: 'info', text: 'Thank you. We are confirming your card with Stripe; this page updates in a moment.' });
    const timer = setInterval(async () => {
      looks.current += 1;
      const out = await load();
      if ((out && out.state !== 'no_card') || looks.current >= 6) {
        clearInterval(timer);
        setResult(out && out.state !== 'no_card'
          ? { tone: 'good', text: 'Your card is saved.' }
          : { tone: 'info', text: 'Your card is still being confirmed. Look again in a few minutes.' });
      }
    }, 2500);
    return () => clearInterval(timer);
  }, [cardReturn, load]);

  const act = async (key, work) => {
    setBusy(key);
    try {
      await work();
    } catch (err) {
      setResult({ tone: 'bad', text: err.message });
    } finally {
      setBusy(null);
      await load();
    }
  };

  const openCardPage = () => act('card', async () => {
    const out = await coachBilling('card_page');
    if (out.result === 'card_page' && out.url) { window.location.assign(out.url); return; }
    setResult(out.result === 'switched_off'
      ? { tone: 'info', text: 'Coach payments are not switched on yet.' }
      : { tone: 'bad', text: 'Stripe\'s card page could not be opened. Please try again.' });
  });

  // The answer may be about another request: a second tap resumes the attempt already under way (PR 304)
  const nameOf = (out, r) => {
    const place = out?.place ?? null;
    if (place != null) return plan.requests.find((x) => Number(x.place) === Number(place))?.name || 'Your client';
    return out?.under_way ? 'Your client' : r.name;
  };
  const accept = (r) => act(r.place, async () => {
    const out = await coachBilling('accept', { place: Number(r.place) });
    setResult(acceptMessage(out, nameOf(out, r), plan.next_bill_pence));
  });

  const checkPayment = () => {
    const r = plan.requests.find((x) => Number(x.place) === Number(plan.attempt?.place));
    // The request ended while being paid for: the attempt is closed for the coach, never charged again (PR 304)
    if (!r) {
      return act('check', async () => {
        setResult({ tone: 'info', text: 'That client\'s request has ended, so the payment attempt is being closed. If your bank had already taken the payment, you have paid for this month and can accept someone else; Cipher is told.' });
      });
    }
    return accept(r);
  };

  const otherCard = () => act('close', async () => {
    const out = await coachBilling('close_attempt');
    setResult(out.result === 'closed' || out.result === 'nothing_open'
      ? { tone: 'info', text: 'That payment attempt is closed and nothing was charged. Add your other card, then accept again.' }
      : acceptMessage(out, 'Your client'));
  });

  const decline = (r) => act(r.place, async () => {
    const out = await declineRequest(Number(r.place));
    if (out === 'closed') toastSuccess(`${r.name}'s request was declined`);
    else setResult({ tone: 'info', text: `${r.name}'s request had already ended.` });
  });

  const leave = () => act('leave', async () => {
    const out = await coachBilling('leave');
    const text = {
      leaving: `Done. Your plan ends on ${when(out.ends_at)}, and you can change your mind until then.`,
      already_leaving: `You have already chosen to stop. Your plan ends on ${when(out.ends_at)}.`,
      ended: out.stripe === 'done'
        ? 'Your plan has ended, and your subscription and unpaid bill are cancelled with Stripe. Your clients now have 7 days to choose what happens next.'
        : 'Your plan has ended, and we are cancelling your subscription and unpaid bill with Stripe. Your clients now have 7 days to choose what happens next.',
      not_paying: 'You are not being billed, so there is nothing to stop.',
      renewal_under_way: 'Your renewal is being charged right now. Please try again in a couple of hours.',
      payment_under_way: 'Your first payment is still going through. Please try again once it has settled.',
      busy: 'Another change to your plan is going through. Try again in a moment.',
      try_again: 'Another change to your plan is going through. Try again in a moment.',
    }[out.result] || 'The page now shows where things stand.';
    setResult({ tone: out.result === 'leaving' || out.result === 'ended' ? 'good' : 'info', text });
  });

  const stay = () => act('stay', async () => {
    const out = await coachBilling('stay');
    // What Stay achieved, as the server reports it: renewing only when Stripe says so (api/lib/coachLeaving.js)
    let next;
    if (out.result === 'plan_ended') next = { tone: 'bad', text: 'Your plan has already ended. Please contact Cipher.' };
    else if (out.result !== 'staying') next = { tone: 'info', text: 'The page now shows where things stand.' };
    else if (out.state === 'stopping') next = { tone: 'good', text: `You are staying. You have no clients right now, so billing stops at your renewal on ${when(out.renews_at, false)} unless you accept someone before then.` };
    else if (out.stripe === 'done') next = { tone: 'good', text: `Welcome back. Your plan carries on and renews on ${when(out.renews_at, false)}.` };
    else if (out.stripe === 'pending') next = { tone: 'info', text: 'You are staying. We are confirming your renewal with Stripe; look again in a few minutes.' };
    else next = { tone: 'bad', text: 'You are staying, but Stripe says your subscription is no longer running, so it cannot renew. Cipher has been told and will be in touch.' };
    setResult(next);
  });

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(plan.code);
      toastSuccess('Code copied');
    } catch {
      toastError('Could not copy. Select the code and copy it instead.');
    }
  };

  const page = (body) => (
    <div style={{ padding: '24px 20px', backgroundColor: theme.bg, minHeight: '100vh', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>
        <button type="button" onClick={onBack} style={{ ...button('quiet'), display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px' }}>
          <ArrowLeft size={16} /> Back
        </button>
        <h1 style={{ color: theme.text, fontSize: '28px', fontWeight: 700, margin: 0 }}>Plan &amp; Code</h1>
        {body}
      </div>
    </div>
  );

  if (failed && !plan) {
    return page(
      <Notice tone="bad" action={<button type="button" style={button()} onClick={load}>Try again</button>}>
        Your plan could not be loaded. Nothing has changed.
      </Notice>,
    );
  }
  if (!plan) return page(<p style={{ color: theme.textMuted }}>Loading your plan…</p>);
  if (plan.result !== 'plan') {
    return page(<p style={{ color: theme.textMuted, marginTop: '12px' }}>You are not on a paying coach plan.</p>);
  }

  const leavable = plan.status === 'active' && !plan.access_ended && !plan.renewal && (plan.state === 'paid' || plan.state === 'unpaid');
  const nextBill = plan.next_bill_pence == null
    ? (plan.state === 'unpaid' || plan.renewal === 'unanswered' ? 'Unpaid'
      : plan.renewal === 'charging_now' ? 'Being charged'
        : plan.state === 'ready' || plan.state === 'paused' || plan.state === 'no_card' ? 'None yet' : 'None')
    : pounds(plan.next_bill_pence);

  return page(
    <>
      <p style={{ color: theme.textMuted, fontSize: '14px', margin: '6px 0 24px' }}>
        You pay for your clients, and they pay nothing. Your bill follows how many clients you have.
      </p>

      {!plan.features_on && (
        <Notice>Coach payments are not switched on yet, so nothing on this page charges you.</Notice>
      )}
      <StateNotice plan={plan} busy={!!busy} onCard={openCardPage} onStay={stay} onCheckPayment={checkPayment} onOtherCard={otherCard} />
      {result && (
        <Notice tone={result.tone} action={result.payUrl
          ? <a href={result.payUrl} target="_blank" rel="noopener noreferrer" style={{ ...button(), textDecoration: 'none' }}>Approve payment</a>
          : <button type="button" style={button('quiet')} onClick={() => setResult(null)}>OK</button>}
        >
          {result.text}
        </Notice>
      )}

      {plan.requests.length > 0 && (
        <div style={{ ...card, marginBottom: '16px' }}>
          <div style={{ ...label, marginBottom: '12px' }}>
            Waiting for you · {plan.requests_total ?? plan.requests.length}
            {plan.requests_total > plan.requests.length && ` (the first ${plan.requests.length} are shown)`}
          </div>
          {plan.requests.map((r) => {
            const line = acceptLine(r, plan);
            return (
              <div key={r.place} data-testid="coach-request" style={{
                display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 0', borderTop: `1px solid ${theme.surface3}`, flexWrap: 'wrap',
              }}>
                <div style={{ flex: '1 1 300px' }}>
                  <div style={{ color: theme.text, fontWeight: 600 }}>{r.name}</div>
                  <div style={{ color: theme.textMuted, fontSize: '13px', marginTop: '2px' }}>{line.what}</div>
                  <div style={{ color: theme.textSubtle, fontSize: '12px', marginTop: '2px' }}>Waits until {when(r.expires_at)}</div>
                </div>
                {line.button && (
                  <button type="button" style={button()} disabled={!!busy || !plan.features_on} onClick={() => accept(r)}>
                    {busy === r.place ? 'Working…' : line.button}
                  </button>
                )}
                {!r.accepted && (
                  <button type="button" style={button('quiet')} disabled={!!busy || !plan.features_on} onClick={() => setConfirm(r)}>Decline</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        <div style={card}>
          <div style={label}>Clients today</div>
          <div style={{ color: theme.text, fontSize: '32px', fontWeight: 700, fontFamily: fonts.mono, marginTop: '8px' }}>{plan.held}</div>
        </div>
        <div style={card}>
          <div style={label}>Highest this month</div>
          <div style={{ color: theme.text, fontSize: '32px', fontWeight: 700, fontFamily: fonts.mono, marginTop: '8px' }}>{plan.highest}</div>
        </div>
        <div style={card}>
          <div style={label}>Next bill{plan.next_bill_pence != null && plan.paid_through ? ` · ${when(plan.paid_through, false)}` : ''}</div>
          <div style={{ color: theme.gold, fontSize: '32px', fontWeight: 700, fontFamily: fonts.mono, marginTop: '8px' }}>{nextBill}</div>
          {plan.next_bill_pence != null && <div style={{ color: theme.textMuted, fontSize: '12px', marginTop: '4px' }}>Estimate</div>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        <div style={card}>
          <div style={label}>Your coach code</div>
          {plan.code ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
              <span style={{ color: theme.gold, fontSize: '26px', fontWeight: 700, letterSpacing: '2px', fontFamily: fonts.mono }}>{plan.code}</span>
              <button type="button" aria-label="Copy code" style={{ ...button('quiet'), padding: '8px' }} onClick={copyCode}><Copy size={16} /></button>
            </div>
          ) : (
            <div style={{ color: theme.textMuted, fontSize: '14px', marginTop: '12px' }}>You have no code yet. Please contact Cipher.</div>
          )}
          <div style={{ color: theme.textMuted, fontSize: '12px', marginTop: '10px', lineHeight: 1.5 }}>
            Clients enter it when they sign up. Nobody joins, and nothing is charged, until you accept them.
            {!plan.takes_clients && plan.status === 'active' && ' Your code is not taking new clients right now.'}
          </div>
        </div>
        <Calculator price={plan.price} start={plan.highest} />
      </div>

      <div style={{ ...card, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
        <CreditCard size={20} color={theme.textMuted} />
        <div style={{ flex: '1 1 240px' }}>
          <div style={label}>Billing</div>
          <div style={{ color: theme.text, fontSize: '14px', marginTop: '4px' }}>{plan.has_card ? 'Card saved with Stripe' : 'No card yet'}</div>
        </div>
        {plan.status === 'active' && !plan.access_ended && (
          <button type="button" style={button('quiet')} disabled={!!busy} onClick={openCardPage}>
            {busy === 'card' ? 'Opening…' : plan.has_card ? 'Update card' : 'Add card'}
          </button>
        )}
      </div>

      {plan.payments.length > 0 && (
        <div style={{ ...card, marginBottom: '16px' }}>
          <div style={{ ...label, marginBottom: '8px' }}>Recent charges</div>
          {plan.payments.map((p) => (
            <div key={`${p.at}-${p.amount_pence}`} style={{ display: 'flex', gap: '12px', padding: '8px 0', borderTop: `1px solid ${theme.surface3}`, fontSize: '14px' }}>
              <span style={{ color: theme.text, flex: 1 }}>{when(p.at, false)}</span>
              <span style={{ color: theme.textMuted }}>{p.clients_counted != null ? `${p.clients_counted} clients` : ''}</span>
              <span style={{ color: theme.textMuted }}>{p.invoice_status === 'paid' ? '' : p.invoice_status}</span>
              <span style={{ color: theme.text, fontFamily: fonts.mono }}>{pounds(p.amount_pence)}</span>
              {p.refunded_pence > 0 && <span style={{ color: theme.success }}>refunded {pounds(p.refunded_pence)}</span>}
            </div>
          ))}
        </div>
      )}

      {leavable && (
        <div style={{ marginTop: '28px' }}>
          <button type="button" style={button('danger')} disabled={!!busy} onClick={() => setConfirm('leave')}>Stop paying</button>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirm === 'leave'}
        onClose={() => setConfirm(null)}
        onConfirm={() => { setConfirm(null); leave(); }}
        title="Stop paying?"
        message={plan.state === 'unpaid'
          ? `Your plan ends now, and we ask Stripe to cancel your subscription and the unpaid bill. If a payment was already going through, Cipher will be in touch. Your ${plan.held === 1 ? 'client gets' : `${plan.held} clients get`} 7 days to choose what happens next, and you lose access to them. This cannot be undone here.`
          : `Your plan ends on ${when(plan.paid_through)}, with nothing more to pay. Until then you keep working with your clients but cannot accept new ones. Then your clients get 7 days to choose what happens next. You can change your mind until your plan ends.`}
        confirmText="Stop paying"
        cancelText="Keep my plan"
        variant="danger"
      />
      <ConfirmDialog
        isOpen={!!confirm && confirm !== 'leave'}
        onClose={() => setConfirm(null)}
        onConfirm={() => { const r = confirm; setConfirm(null); decline(r); }}
        title={confirm && confirm !== 'leave' ? `Decline ${confirm.name}?` : ''}
        message="Their request closes, and they can join Cipher without a coach. Nothing is charged to you."
        confirmText="Decline"
        cancelText="Keep waiting"
        variant="warning"
      />
    </>,
  );
};

export default CoachPlanPage;
